<template>
    <vx-page-layout title="Integration Log">
        <template v-slot:subtitle>
            <a :href="backUrl" class="no-underline! text-sky-600! text-base font-normal">
                <font-awesome-icon widthAuto class="mr-1" :icon="faArrowLeft"></font-awesome-icon>
                Integration Logs
            </a>
        </template>

        <div v-if="loading" class="py-8 flex flex-col items-center gap-2">
            <vx-icon-loading class="w-8 h-8" spin></vx-icon-loading>
            <span class="text-sm text-slate-400">Results may take a few seconds to load</span>
        </div>

        <vx-alert v-else-if="error" color="danger">
            {{ error }}
        </vx-alert>

        <vx-box
            color="info"
            v-else-if="!data"
            class="flex flex-col px-8 py-20 gap-6 items-center text-xl text-slate-500"
        >
            Integration log entry not found.
        </vx-box>

        <div v-else class="grid gap-6 grid-cols-1 lg:grid-cols-12">
            <integration-log-metadata-card class="lg:col-span-6" :entry="data" />
            <integration-log-status-card class="lg:col-span-6" :entry="data" />
            <integration-log-errors-card
                v-if="data.errors && data.errors.length"
                class="lg:col-span-12"
                :entry="data"
            />
            <integration-log-payload-card class="lg:col-span-12" :entry="data" />
            <integration-log-extracted-data-card class="lg:col-span-12" :entry="data" />
            <integration-log-events-card class="lg:col-span-12" :entry="data" />
        </div>
    </vx-page-layout>
</template>

<script lang="ts" setup>
import { VxIconLoading, VxBox, VxAlert, VxPageLayout } from '@voxie/frontend-components';
import { faArrowLeft } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import IntegrationLogMetadataCard from './cards/IntegrationLogMetadataCard.vue';
import IntegrationLogStatusCard from './cards/IntegrationLogStatusCard.vue';
import IntegrationLogPayloadCard from './cards/IntegrationLogPayloadCard.vue';
import IntegrationLogExtractedDataCard from './cards/IntegrationLogExtractedDataCard.vue';
import IntegrationLogEventsCard from './cards/IntegrationLogEventsCard.vue';
import IntegrationLogErrorsCard from './cards/IntegrationLogErrorsCard.vue';
import { integrationLogsClient, type IntegrationLogEntry } from '~/services/integrationLogsClient';
import { computed, onMounted, ref } from 'vue';

const props = defineProps<{
    ptr: string;
    vendor: string;
}>();

const teamId = Spark.state.currentTeam.id;

const loading = ref(false);
const data = ref<IntegrationLogEntry | null>(null);
const error = ref<string | null>(null);
const backUrl = computed(() => `/settings/integrations/${props.vendor}/logs`);

async function fetchEntry(): Promise<void> {
    if ('' === props.ptr) {
        data.value = null;
        return;
    }

    loading.value = true;
    error.value = null;

    try {
        const response = await integrationLogsClient.getEntry(teamId, props.ptr);
        data.value = response.data.data;
    } catch {
        error.value = 'An error occurred. Please try again.';
    } finally {
        loading.value = false;
    }
}

onMounted(() => {
    fetchEntry();
});
</script>
